<template>
  <el-dialog
    v-model="visible"
    :title="isEdit ? '메인오더 수정' : '신규 메인오더'"
    width="700px"
    destroy-on-close
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="요청번호" prop="request_number">
            <el-input v-model="form.request_number" placeholder="자동 생성" :disabled="isEdit" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="브랜드" prop="brand">
            <el-select v-model="form.brand" placeholder="브랜드 선택" style="width: 100%">
              <el-option
                v-for="brand in brands"
                :key="brand.id"
                :label="brand.name"
                :value="brand.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="법인" prop="company">
            <el-select v-model="form.company" filterable clearable placeholder="법인 선택" style="width: 100%">
              <el-option v-for="c in codeStore.ourCompanys" :key="c.id" :label="c.name" :value="c.id" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="디자인" prop="design">
            <el-select
              v-model="form.design"
              placeholder="스타일번호 검색"
              filterable
              remote
              :remote-method="searchDesigns"
              :loading="designLoading"
              style="width: 100%"
            >
              <el-option
                v-for="design in designs"
                :key="design.id"
                :label="`${design.main_style_number || design.style_number || ''} - ${design.product_name || ''}`"
                :value="design.id"
              >
                <span>{{ design.main_style_number || design.style_number }}</span>
                <span style="color:#999; margin-left:8px">{{ design.product_name || '' }}</span>
                <span style="color:#999; margin-left:8px">{{ design.brand_name || '' }}</span>
              </el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="발주수량" prop="order_quantity">
            <el-input-number v-model="form.order_quantity" :min="1" style="width: 100%" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="여유율(%)">
            <el-input-number
              v-model="form.allowance_rate"
              :min="0" :max="50" :step="0.5" :precision="2"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="생산수량">
            <el-input :model-value="productionQuantity.toLocaleString()" disabled>
              <template #append>PCS</template>
            </el-input>
            <div style="color: #909399; font-size: 12px; margin-top: 2px">
              발주 {{ (form.order_quantity || 0).toLocaleString() }} + 여유 {{ (productionQuantity - (form.order_quantity || 0)).toLocaleString() }}
            </div>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="요청일" prop="request_date">
            <el-date-picker
              v-model="form.request_date"
              type="date"
              placeholder="요청일"
              value-format="YYYY-MM-DD"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="납기일" prop="delivery_date">
            <el-date-picker
              v-model="form.delivery_date"
              type="date"
              placeholder="납기일"
              value-format="YYYY-MM-DD"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="제조공장" prop="manufacturer">
            <el-select v-model="form.manufacturer" placeholder="제조공장 선택" filterable clearable style="width: 100%">
              <el-option
                v-for="mfg in manufacturers"
                :key="mfg.id"
                :label="mfg.name"
                :value="mfg.id"
              >
                <span>{{ mfg.name }}</span>
                <span v-if="mfg.country" style="color:#999; margin-left:8px">{{ mfg.country }}</span>
              </el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col v-if="isEdit" :span="12">
          <el-form-item label="상태" prop="status">
            <el-select v-model="form.status" style="width: 100%">
              <el-option label="요청접수" value="REQUESTED" />
              <el-option label="자재발주중" value="MATERIAL_ORDERING" />
              <el-option label="생산중" value="IN_PRODUCTION" />
              <el-option label="선적중" value="SHIPPING" />
              <el-option label="납품완료" value="DELIVERED" />
              <el-option label="정산완료" value="COMPLETED" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="혼용율">
            <el-input v-model="form.mix_ratio" placeholder="예: 면 95% 폴리 5%" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="견적서" prop="quotation">
            <el-select v-model="form.quotation" placeholder="견적서 선택" clearable style="width: 100%">
              <el-option
                v-for="q in quotations"
                :key="q.id"
                :label="q.quotation_number"
                :value="q.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row v-if="isEdit" :gutter="20">
        <el-col :span="12">
          <!-- MD담당 지름길 (2026-08-02 사이먼): 참여자 탭의 MD 역할과 동일 데이터.
               나머지 역할(자재/생산/무역/품질)은 참여자 탭에서 관리 -->
          <el-form-item label="MD담당">
            <el-select v-model="mdManager" filterable clearable placeholder="담당자 선택 — 상세 관리는 참여자 탭" style="width: 100%">
              <el-option
                v-for="emp in employees"
                :key="emp.id"
                :label="`${emp.get_full_name || emp.username} (${emp.department_name || '-'})`"
                :value="emp.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="가공방법">
        <el-input v-model="form.process_method" type="textarea" :rows="2" placeholder="예: 워싱 후 텀블 건조" />
      </el-form-item>

      <el-form-item label="비고">
        <el-input v-model="form.remarks" type="textarea" :rows="3" />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="visible = false">취소</el-button>
      <el-button type="primary" @click="handleSubmit">저장</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, watch, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import { useCodeStore } from '@/stores/codeStore'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  editData: {
    type: Object,
    default: null
  },
  defaultData: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const codeStore = useCodeStore()
const visible = ref(props.modelValue)
const formRef = ref(null)
const brands = ref([])
const designs = ref([])
const manufacturers = ref([])
const quotations = ref([])
const designLoading = ref(false)

const isEdit = computed(() => !!props.editData?.id)

const form = reactive({
  request_number: '',
  company: null,
  brand: null,
  design: null,
  order_quantity: 1000,
  allowance_rate: 0,
  request_date: new Date().toISOString().split('T')[0],
  delivery_date: '',
  manufacturer: null,
  status: 'REQUESTED',
  mix_ratio: '',
  process_method: '',
  quotation: null,
  remarks: ''
})

const productionQuantity = computed(() => {
  const qty = form.order_quantity || 0
  const rate = parseFloat(form.allowance_rate) || 0
  return Math.ceil(qty * (1 + rate / 100))
})

const rules = {
  brand: [{ required: true, message: '브랜드를 선택하세요', trigger: 'change' }],
  design: [{ required: true, message: '디자인을 선택하세요', trigger: 'change' }],
  order_quantity: [{ required: true, message: '발주수량을 입력하세요', trigger: 'blur' }],
  delivery_date: [{ required: true, message: '납기일을 선택하세요', trigger: 'change' }]
}

const fetchBrands = async () => {
  try {
    const response = await axiosInstance.get('brands/')
    brands.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch brands:', error)
  }
}

const fetchDesigns = async (search = '') => {
  try {
    designLoading.value = true
    const params = { page_size: 20 }
    if (search) params.search = search
    const response = await axiosInstance.get('designs/', { params })
    designs.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch designs:', error)
  } finally {
    designLoading.value = false
  }
}

const searchDesigns = (query) => {
  fetchDesigns(query)
}

const fetchManufacturers = async () => {
  try {
    const response = await axiosInstance.get('production/manufacturers/', {
      params: { active: true }
    })
    manufacturers.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch manufacturers:', error)
  }
}

const fetchQuotations = async () => {
  try {
    const response = await axiosInstance.get('v2/estimate/quotations/', {
      params: { status: 'CONFIRMED' }
    })
    quotations.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch quotations:', error)
  }
}

const resetForm = () => {
  form.request_number = ''
  form.company = null
  form.brand = null
  form.design = null
  form.order_quantity = 1000
  form.allowance_rate = 0
  form.request_date = new Date().toISOString().split('T')[0]
  form.delivery_date = ''
  form.manufacturer = null
  form.status = 'REQUESTED'
  form.mix_ratio = ''
  form.process_method = ''
  form.quotation = null
  form.remarks = ''
}

// ── MD담당 지름길 (2026-08-02) ──
const employees = ref([])
const mdManager = ref(null)
const mdManagerLoaded = ref(null)   // 저장 시 변경 여부 판단

const loadEmployees = async () => {
  if (employees.value.length) return
  try {
    const res = await axiosInstance.get('employees/', { params: { page_size: 500 } })
    employees.value = res.data.results || res.data
  } catch (e) {
    console.error('직원 목록 조회 실패:', e)
  }
}

const loadMdManager = async () => {
  mdManager.value = null
  mdManagerLoaded.value = null
  if (!props.editData?.id) return
  try {
    const res = await axiosInstance.get(`production/main-requests/${props.editData.id}/participants/`)
    const md = (res.data || []).find(p => p.role === 'MD')
    mdManager.value = md ? md.employee : null
    mdManagerLoaded.value = mdManager.value
  } catch (e) {
    console.error('참여자 조회 실패:', e)
  }
}

const loadEditData = async () => {
  if (props.editData) {
    loadEmployees()
    loadMdManager()
    form.request_number = props.editData.request_number
    form.company = props.editData.company || null
    form.brand = props.editData.brand
    form.design = props.editData.design
    form.order_quantity = props.editData.order_quantity
    form.allowance_rate = props.editData.allowance_rate || 0
    form.request_date = props.editData.request_date
    form.delivery_date = props.editData.delivery_date
    form.manufacturer = props.editData.manufacturer
    form.status = props.editData.status
    form.mix_ratio = props.editData.mix_ratio || ''
    form.process_method = props.editData.process_method || ''
    form.quotation = props.editData.quotation || null
    form.remarks = props.editData.remarks || ''

    // 수정 시 현재 디자인이 드롭다운에 표시되도록 조회
    if (form.design && !designs.value.find(d => d.id === form.design)) {
      try {
        const resp = await axiosInstance.get(`designs/${form.design}/`)
        if (resp.data && !designs.value.find(d => d.id === resp.data.id)) {
          designs.value = [resp.data, ...designs.value]
        }
      } catch (e) {
        console.error('Failed to fetch edit design:', e)
      }
    }
  }
}

const handleSubmit = async () => {
  await formRef.value.validate()

  try {
    if (isEdit.value) {
      await axiosInstance.put(`production/main-requests/${props.editData.id}/`, form)
      // MD담당 변경분 동기화 (2026-08-02) — 참여자 탭과 같은 데이터
      if (mdManager.value !== mdManagerLoaded.value) {
        await axiosInstance.post(`production/main-requests/${props.editData.id}/set-manager/`, {
          employee: mdManager.value,
        })
      }
      ElMessage.success('메인오더가 수정되었습니다.')
    } else {
      // 신규 생성 시 status 제외 (모델 기본값 REQUESTED 사용)
      const createData = Object.fromEntries(
        Object.entries(form).filter(([k]) => k !== 'status')
      )
      await axiosInstance.post('production/main-requests/', createData)
      ElMessage.success('메인오더가 생성되었습니다.')
    }
    emit('saved')
    visible.value = false
  } catch (error) {
    if (error.response?.data) {
      const errors = Object.values(error.response.data).flat().join(', ')
      ElMessage.error(errors || '저장에 실패했습니다.')
    } else {
      ElMessage.error('저장에 실패했습니다.')
    }
    console.error(error)
  }
}

watch(() => props.modelValue, (val) => {
  visible.value = val
  if (val) {
    if (props.editData) {
      loadEditData()
    } else {
      resetForm()
      if (props.defaultData) {
        if (props.defaultData.brand) form.brand = props.defaultData.brand
        if (props.defaultData.design) form.design = props.defaultData.design
        if (props.defaultData.order_quantity) form.order_quantity = props.defaultData.order_quantity
        if (props.defaultData.delivery_date) form.delivery_date = props.defaultData.delivery_date
      }
    }
  }
})

watch(visible, (val) => {
  emit('update:modelValue', val)
})

onMounted(() => {
  fetchBrands()
  fetchDesigns()
  fetchManufacturers()
  fetchQuotations()
  codeStore.fetchOurCompanys()
})
</script>
